<script setup lang="ts">
import { FileIcon } from "lucide-vue-next";
import { onBeforeUnmount, onMounted, type Ref, ref } from "vue";
import type { ProductAttachmentResponse } from "../../../responses/ProductAttachmentResponse.ts";
import type { ProductResponse } from "../../../responses/ProductResponse";
import type { ProductVariantResponse } from "../../../responses/ProductVariantResponse";
import { label } from "../../mixins/translations";
import {
    type ProductVariantProperties,
    useProductStore,
} from "../../stores/product";

const tabs: Ref<string[]> = ref([]);

interface Props {
    product: ProductResponse;
}

const productStore = useProductStore();
const props = defineProps<Props>();
const selectedTab: Ref<string> = ref("description");
const selectedVariantProperties: Ref<ProductVariantProperties> = ref({});
const faqs = ref(props.product.faqs); //for toggling faq questions
const attachments: Ref<ProductAttachmentResponse[]> = ref(
    props.product.attachments,
);

const handleSelectedVariantChanged = (variant: ProductVariantResponse) => {
    if (!variant) {
        return;
    }

    productStore
        .getVariantProperties(variant)
        .then((response) => {
            selectedVariantProperties.value = response;
        })
        .catch((e) => {
            console.error(e);
        });
};

onMounted(() => {
    productStore.events.on(
        "selectedVariantChanged",
        handleSelectedVariantChanged,
    );

    if (productStore.selectedVariant === null && props.product?.variants?.[0]) {
        productStore.setSelectedVariant(props.product.variants[0]);
    }

    if (props.product.description_long) tabs.value.push("description");
    if (selectedVariantProperties) tabs.value.push("properties");
    if (faqs.value.length) tabs.value.push("faqs");
    if (attachments.value.length) tabs.value.push("files");
});

onBeforeUnmount(() => {
    productStore.events.off(
        "selectedVariantChanged",
        handleSelectedVariantChanged,
    );
});
</script>
<template>
    <div class="flex gap-5 md:gap-10 items-center md:items-start flex-row relative">
        <h3 class="text-base-200 py-3 md:py-4 cursor-pointer text-lg lg:text-xl leading-8" v-for="tab in tabs"
            :class="{'font-bold text-primary border-b-2 border-primary': selectedTab === tab, 'font-medium': selectedTab !== tab}"
            @click.prevent="selectedTab = tab;">
            {{ label('site.product.detail.' + tab) }}
        </h3>
    </div>
    <div class="mt-6" :class="{'hidden': selectedTab !== 'description'}">
        <p class="font-normal leading-relaxed text-base mt-5 wysiwyg-content" v-html="product.description_long">
        </p>
    </div>
    <div class="mt-6" :class="{'hidden': selectedTab !== 'properties'}">
        <div class="max-w-2xl font-normal leading-relaxed text-base mt-5 specifications"
             v-for="(productVariantProperties, category) in selectedVariantProperties">
            <h3 class="text-lg text-[#1A1A1C] font-medium">{{ category }}</h3>
            <ul class="my-4">
                <li v-for="productVariantProperty in productVariantProperties"
                    class="flex flex-row leading-8 specificationItem odd:bg-secondary-200">
                    <span class="w-3/6 text-base px-4 p-2" v-html="productVariantProperty?.name"></span>
                    <span class="w-3/6 text-base px-4 p-2" v-html="productVariantProperty?.value"></span>
                </li>
            </ul>
        </div>
    </div>
    <div class="mt-6" :class="{'hidden': selectedTab !== 'faqs'}">
        <div class="font-normal leading-relaxed text-base mt-5">
            <div v-for="faq in faqs" class="pb-2">
                <h4 class="hover:cursor-pointer text-base lg:text-lg border-b border-secondary-100 pb-2 flex align-center w-full"
                    @click="faq.toggled = !faq.toggled">
                    <span class="icon">
                        <svg id="icon-faq" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"
                             fill="none" stroke="currentColor"
                             stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                             class="lucide text-primary mt-1 lucide-chevron-right">
                            <path v-show="!faq.toggled" d="m9 18 6-6-6-6"/>
                            <path v-show="faq.toggled" d="m6 9 6 6 6-6"/>
                        </svg>
                    </span>
                    <span class="ml-2 text-secondary-900 font-medium" v-text="faq.question"></span>
                </h4>
                <div class="text-lg leading-8 mb-2 text-gray-900 collapse-content" v-show="faq.toggled"
                     v-html="faq.answer"></div>
            </div>
        </div>
    </div>
    <div class="mt-6" :class="{'hidden': selectedTab !== 'files'}">
        <div class="font-normal leading-relaxed text-base mt-5">
            <a :href="attachment.download_link" :target="attachment.file_mime_type === 'application/pdf' ? '_blank' : '_self'" v-for="attachment in attachments" class="flex flex-row gap-1 cursor-pointer items-center" :key="attachment.id">
                <FileIcon class="w-4 h-4" />
                <span v-text="attachment.button_label"></span>
            </a>
        </div>
    </div>
</template>
